import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import CelebrationOverlay from '../celebrations/CelebrationOverlay';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

export default function CelebrationSettings() {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [demo, setDemo] = useState(null);
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([
      fetch(`${API}/api/celebrations/settings`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/celebrations/history?limit=50`, { headers: authHeaders() }).then(r => r.json()),
    ]);
    setSettings(s);
    setHistory(h.events || []);
  }, []);
  useEffect(() => { load().catch(() => toast.error('Failed to load celebration settings')); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/celebrations/settings`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error();
      setSettings(await r.json());
      toast.success('Celebration settings saved');
    } catch { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const searchEmployees = async (q) => {
    setEmpSearch(q);
    if (q.length < 2) { setEmpResults([]); return; }
    const r = await fetch(`${API}/api/hr/employees?search=${encodeURIComponent(q)}&limit=8`, { headers: authHeaders() });
    const d = await r.json();
    setEmpResults(d.data || []);
  };

  const setOptOut = async (employee_id, opt_out) => {
    const r = await fetch(`${API}/api/celebrations/opt-out`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ employee_id, opt_out }),
    });
    if (r.ok) {
      toast.success(opt_out ? 'Employee excluded from celebrations' : 'Employee included in celebrations');
      setEmpResults(res => res.map(e => e.employee_id === employee_id ? { ...e, celebrations_opt_out: opt_out } : e));
    } else toast.error('Update failed');
  };

  const runNow = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/celebrations/run-now`, { method: 'POST', headers: authHeaders() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      toast.success(d.created
        ? `Created ${d.created} celebration(s) for ${d.date} — notified ${d.recipients} users`
        : 'No new celebrations today (already evaluated or no matching dates)');
      await load();
    } catch { toast.error('Evaluation failed'); }
    setSaving(false);
  };

  const resetViews = async (id) => {
    const r = await fetch(`${API}/api/celebrations/${id}/reset-views`, { method: 'POST', headers: authHeaders() });
    if (r.ok) {
      const d = await r.json();
      toast.success(`Cleared ${d.views_cleared} view record(s) — overlay will replay for everyone`);
      await load();
    } else toast.error('Reset failed');
  };

  const deleteCelebration = async (id) => {
    const r = await fetch(`${API}/api/celebrations/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (r.ok) {
      toast.success('Celebration removed (event, views and bell notification)');
      await load();
    } else toast.error('Delete failed');
  };

  const previewAnimation = (etype) => setDemo([{
    id: 'demo', event_type: etype, employee_name: 'Alex Morgan', years: etype === 'anniversary' ? 5 : null,
    message: etype === 'birthday'
      ? (settings.birthday_template || '').replace('[Employee Name]', 'Alex Morgan')
      : (settings.anniversary_template || '').replace('[Employee Name]', 'Alex Morgan')
          .replace('[Number]', '5').replace('[Company]', settings.company_name),
  }]);

  if (!settings) return <div className="p-8 text-slate-500">Loading…</div>;

  const Toggle = ({ label, desc, field, testid }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100">
      <div>
        <p className="font-medium text-slate-800">{label}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
      <Switch checked={!!settings[field]} disabled={saving}
              onCheckedChange={(v) => save({ [field]: v })} data-testid={testid} />
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="celebration-settings-page">
      {demo && <CelebrationOverlay demoEvents={demo} onDemoClose={() => setDemo(null)} />}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">🎉 Team Celebrations</h1>
          <p className="text-sm text-slate-500">Automatic birthday & work-anniversary notifications with premium celebration animations.</p>
        </div>
        <button onClick={runNow} disabled={saving} data-testid="run-evaluation-now-btn"
                className="shrink-0 px-4 py-2 rounded-full text-sm font-semibold text-white bg-slate-800 hover:bg-slate-700 disabled:opacity-50">
          Run Daily Evaluation Now
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-slate-800 mb-2">Event Types & Animations</h2>
        <Toggle label="Birthday notifications" desc="Announce employee birthdays to the whole team" field="birthdays_enabled" testid="toggle-birthdays" />
        <Toggle label="Work-anniversary notifications" desc="Celebrate completed service years" field="anniversaries_enabled" testid="toggle-anniversaries" />
        <Toggle label="Celebration animations" desc="Full-screen premium animation (bell notification always remains)" field="animations_enabled" testid="toggle-animations" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <h2 className="font-semibold text-slate-800">Messages</h2>
        <div>
          <label className="text-sm font-medium text-slate-700">Company name</label>
          <input className="mt-1 w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm"
                 defaultValue={settings.company_name} data-testid="company-name-input"
                 onBlur={(e) => e.target.value !== settings.company_name && save({ company_name: e.target.value })} />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Birthday message — placeholders: [Employee Name]</label>
          <textarea className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" rows={2}
                    defaultValue={settings.birthday_template} data-testid="birthday-template-input"
                    onBlur={(e) => e.target.value !== settings.birthday_template && save({ birthday_template: e.target.value })} />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Anniversary message — placeholders: [Employee Name], [Number], [Company]</label>
          <textarea className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" rows={2}
                    defaultValue={settings.anniversary_template} data-testid="anniversary-template-input"
                    onBlur={(e) => e.target.value !== settings.anniversary_template && save({ anniversary_template: e.target.value })} />
        </div>
        <div className="flex gap-3 pt-1">
          <button onClick={() => previewAnimation('birthday')} data-testid="preview-birthday-btn"
                  className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-90">
            Preview Birthday Animation
          </button>
          <button onClick={() => previewAnimation('anniversary')} data-testid="preview-anniversary-btn"
                  className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-sky-600 hover:opacity-90">
            Preview Anniversary Animation
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-slate-800 mb-1">Privacy Exclusions</h2>
        <p className="text-xs text-slate-500 mb-3">Excluded employees are never announced to anyone. Age, birth year, and full DOB are never displayed.</p>
        <input className="w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm"
               placeholder="Search employee by name or email…" value={empSearch}
               onChange={(e) => searchEmployees(e.target.value)} data-testid="exclusion-search-input" />
        {empResults.map(e => (
          <div key={e.employee_id} className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-700">{e.full_name} <span className="text-slate-400">({e.employee_id})</span></span>
            <button onClick={() => setOptOut(e.employee_id, !e.celebrations_opt_out)}
                    data-testid={`exclusion-toggle-${e.employee_id}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full ${e.celebrations_opt_out
                      ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {e.celebrations_opt_out ? 'Excluded — click to include' : 'Included — click to exclude'}
            </button>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-slate-800 mb-3">Delivery History</h2>
        <table className="w-full text-sm" data-testid="celebration-history-table">
          <thead>
            <tr className="tf-thead text-left text-xs uppercase">
              <th className="py-2">Date</th><th>Event</th><th>Employee</th>
              <th className="text-right">Recipients</th><th className="text-right">Viewed</th><th className="text-right">Dismissed</th><th>Status</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-slate-400">No celebrations yet</td></tr>
            )}
            {history.map(e => (
              <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="py-2">{e.event_date}</td>
                <td>
                  {e.event_type === 'birthday' ? '🎉 Birthday' : `🎊 ${e.years}-yr Anniversary`}
                  {e.recipient_employed === false && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-2 py-px text-[10px] font-semibold" data-testid="celebration-suppressed-tag" title="Employee is no longer employed — hidden from Today's Celebrations; new wishes are closed">Suppressed · exited</span>
                  )}
                </td>
                <td>{e.employee_name}</td>
                <td className="text-right">{e.recipient_count}</td>
                <td className="text-right">{e.viewed_count}</td>
                <td className="text-right">{e.dismissed_count}</td>
                <td><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{e.delivery_status}</span></td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => resetViews(e.id)} data-testid={`reset-views-${e.id}`}
                          title="Clear viewed/dismissed records so the overlay replays for everyone"
                          className="text-xs font-semibold text-sky-700 hover:underline mr-3">Reset views</button>
                  <button onClick={() => deleteCelebration(e.id)} data-testid={`delete-celebration-${e.id}`}
                          title="Remove event, views and bell notification (frees the once-per-year slot)"
                          className="text-xs font-semibold text-rose-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
